"use client";

import NextImage from "next/image";
import { motion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";

import { TrustpilotMicroTrustScore } from "@/components/global/TrustpilotWidgets";
import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { CountdownBox, EventFooter, TicketCTA, wave, type EventLayout } from "@/components/pageComponents/event/EventShared";

import columns from "@/public/event/columns.png";

import { cn } from "@/lib/utils";

const ease = [0.25, 0.1, 0.25, 1] as const;

/** Final dark CTA: title, stats, countdown, ticket button, trust line, footer. */
export function EventFinalCta({ data, layout = "event" }: { data: PageEventSchema; layout?: EventLayout }) {
  const monthly = layout === "monthly";
  const germanMonthly = monthly && stegaClean(data.settings.language ?? "en") === "de";
  const stats = data.s6Stats ?? [];
  const footerLinks = data.s6FooterLinks ?? [];

  return (
    <section className={cn("relative flex w-full flex-col items-center overflow-clip rounded-t-[1.25rem] bg-[#19362e] px-[2rem] pt-[9rem] pb-[26rem] lg:px-0 lg:pt-[15rem] lg:pb-[18rem]", monthly && "lg:min-h-[108.625rem] max-lg:min-h-[calc(106.625*var(--page-rem))] max-lg:px-[calc(2*var(--page-rem))] max-lg:pt-[calc(8.94989*var(--page-rem))] max-lg:pb-[calc(24*var(--page-rem))]", germanMonthly && "max-lg:justify-between max-lg:pb-0")}>
      {/* Glowing chart line */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[60rem] left-[-4.375rem] h-[156.25rem] w-[271.875rem] lg:top-[64.4rem]">
        <div className="absolute inset-[-47.12%_-27.07%]">
          <NextImage
            src={wave}
            alt=""
            className="block !h-full !w-full !max-w-none"
          />
        </div>
      </div>
      {/* Column ruins, left + mirrored right (desktop only) */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[2rem] left-[-71.4rem] hidden h-[131.2rem] w-[168.125rem] lg:block">
        <NextImage
          src={columns}
          alt=""
          className="absolute inset-0 !h-full !w-full !max-w-none object-cover"
        />
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute top-[2rem] right-[-71.4rem] hidden h-[131.2rem] w-[168.125rem] -scale-x-100 lg:block">
        <NextImage
          src={columns}
          alt=""
          className="absolute inset-0 !h-full !w-full !max-w-none object-cover"
        />
      </div>

      <div className={cn("relative z-10 flex w-full flex-col items-center gap-[3.25rem] lg:w-[101.375rem] lg:gap-[4rem]", monthly && "max-lg:w-[min(calc(49.75*var(--page-rem)),100%)] max-lg:gap-[calc(4*var(--page-rem))]")}>
        <div className={cn("flex flex-col gap-[1rem]", monthly && "max-lg:w-full max-lg:gap-0")}>
          {(data.s6TitleRegular || data.s6TitleItalic) && (
            <h2 className={cn("mb-0 text-center font-blacker text-[4.5rem] leading-[1.12] font-normal tracking-[-0.135rem] text-white lg:text-[6rem] lg:tracking-[-0.18rem] [&_span]:font-blacker", monthly && "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic] max-lg:text-[calc(4.5*var(--page-rem))] max-lg:tracking-[calc(-0.18*var(--page-rem))]")}>
              {data.s6TitleRegular && <span>{data.s6TitleRegular} </span>}
              {data.s6TitleItalic && <span className="italic">{data.s6TitleItalic}</span>}
            </h2>
          )}
          {data.s6Description && (
            <p className={cn("mb-0 w-[min(49rem,100%)] text-center font-geist text-[2.25rem] leading-[1.5] font-normal text-white/70 lg:w-[93.125rem]", monthly && "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic] lg:mt-[3rem] max-lg:mt-[calc(3*var(--page-rem))] max-lg:w-full max-lg:text-[calc(2.25*var(--page-rem))]")}>
              {data.s6Description}
            </p>
          )}
        </div>

        {!!stats.length && (
          <div className={cn("flex flex-wrap items-stretch justify-center gap-[2.25rem] lg:gap-[6.625rem]", monthly && "max-lg:gap-[calc(2.25*var(--page-rem))]")}>
            {stats.slice(0, 3).map((stat, i) => (
              <div
                key={stat._key ?? i}
                className="contents">
                {i > 0 && <div className="w-px self-stretch bg-white/20" />}
                <div className={"flex flex-col items-center justify-center gap-[1.125rem] whitespace-nowrap text-white lg:gap-[1.375rem]"}>
                  <p className={cn("mb-0 font-blacker text-[3rem] leading-[1.1] font-normal tracking-[-0.09rem] lg:text-[4.5rem]", monthly && "max-lg:[text-box-trim:trim-both] max-lg:[text-box-edge:cap_alphabetic] max-lg:text-[calc(3*var(--page-rem))] max-lg:tracking-[calc(-0.06*var(--page-rem))]")}>
                    {stat.value}
                  </p>
                  <p className={cn("mb-0 font-geist text-[1.66rem] leading-[1.4] font-normal opacity-60 lg:text-[2rem]", monthly && "max-lg:text-[calc(1.659625*var(--page-rem))]")}>
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className={cn("flex w-full flex-col items-center gap-[1rem] lg:w-[62.5rem] lg:gap-[2rem]", monthly && "max-lg:gap-[calc(1.5*var(--page-rem))]")}>
          <CountdownBox
            label={data.countdownLabel}
            target={data.countdownTarget}
            language={monthly ? data.settings.language : undefined}
            variant="light"
            layout={layout}
            className={monthly ? "max-lg:w-full max-lg:gap-[0.875rem]" : "w-[min(46.875rem,100%)] gap-[0.875rem] px-[2.4rem] pt-[2.4rem] pb-[1.75rem] lg:gap-[1.5rem] lg:px-[2.875rem] lg:pt-[2.875rem] lg:pb-[2.5rem]"}
          />
          <TicketCTA
            data={data.s6Cta}
            layout={layout}
            className={monthly ? "max-lg:mt-[calc(0.5*var(--page-rem))] max-lg:w-[min(calc(49.75*var(--page-rem)),100%)] max-lg:h-[calc(7.75*var(--page-rem))] lg:mt-0 lg:w-[min(42.875rem,100%)]" : "mt-[0.625rem] w-[min(46.875rem,100%)] lg:mt-0 lg:w-[47.625rem]"}
          />
          {/* Same Trustpilot block as the homepage hero (components/pageComponents/home/HeroSection.tsx) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.65 }}
            className={`${data?.settings?.language === "de" ? "lg:w-[60rem] w-[44rem]" : "lg:w-[50rem] w-[44rem]"}`}>
            <TrustpilotMicroTrustScore locale={data?.settings?.language === "de" ? "de-DE" : "en-US"} />
          </motion.div>
        </div>
      </div>

      <EventFooter
        copyright={data.s6FooterCopyright}
        layout={layout}
        links={footerLinks}
        linksClassName={germanMonthly ? "max-lg:text-[calc(1.625*var(--page-rem))] max-lg:leading-[calc(3*var(--page-rem))] max-lg:whitespace-nowrap" : undefined}
        className={germanMonthly ? "max-lg:relative max-lg:mt-[calc(5.75*var(--page-rem))] max-lg:h-auto max-lg:min-h-[calc(18.25*var(--page-rem))] max-lg:w-[calc(100%+4*var(--page-rem))]" : undefined}
      />
    </section>
  );
  <div></div>;
}
